import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { trackEvent } from "../../utils/analytics";

function ColocationCalculator() {
  const [units, setUnits] = useState(1);
  const [power, setPower] = useState(1);
  const [bandwidth, setBandwidth] = useState(1);
  const [cost, setCost] = useState(null);

  const calculateCost = () => {
    const rackCostPerU = 2000; // ₹ per U
    const powerCostPerKW = 8000; // ₹ per kW
    const bandwidthCostPerTB = 500; // ₹ per TB

    const total =
      units * rackCostPerU +
      power * powerCostPerKW +
      bandwidth * bandwidthCostPerTB;

    setCost(total);
    trackEvent("use_colocation_calculator", { units, power, bandwidth });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
      <Helmet>
        <title>Colocation Cost Calculator | QuantaRock Systems</title>
        <meta
          name="description"
          content="Estimate your monthly colocation cost based on rack units, power, and bandwidth requirements with QuantaRock's free colocation cost calculator."
        />
        <meta name="robots" content="index,follow" />
        <link
          rel="canonical"
          href="https://quantarock.in/colocation-cost-calculator"
        />
        <meta
          property="og:title"
          content="Colocation Cost Calculator | QuantaRock Systems"
        />
        <meta
          property="og:description"
          content="Estimate your monthly colocation cost based on rack units, power, and bandwidth requirements."
        />
        <meta
          property="og:url"
          content="https://quantarock.in/colocation-cost-calculator"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://quantarock.in/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Colocation Cost Calculator | QuantaRock Systems"
        />
        <meta
          name="twitter:description"
          content="Estimate your monthly colocation cost based on rack units, power, and bandwidth requirements."
        />
      </Helmet>
      <div className="w-full max-w-2xl shadow-xl rounded-2xl bg-white">
        <div className="p-6 space-y-6">
          <h1 className="text-2xl font-bold text-center">
            Colocation Cost Calculator
          </h1>

          <div className="grid gap-4">
            {/* Rack Units */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Rack Units (U)
              </label>
              <input
                type="number"
                className="w-full border rounded-lg p-2"
                value={units}
                onChange={(e) => setUnits(Number(e.target.value))}
              />
            </div>

            {/* Power */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Power Requirement (kW)
              </label>
              <input
                type="number"
                className="w-full border rounded-lg p-2"
                value={power}
                onChange={(e) => setPower(Number(e.target.value))}
              />
            </div>

            {/* Bandwidth */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Bandwidth Usage (TB/month)
              </label>
              <input
                type="number"
                className="w-full border rounded-lg p-2"
                value={bandwidth}
                onChange={(e) => setBandwidth(Number(e.target.value))}
              />
            </div>
          </div>

          {/* Button */}
          <button
            className="w-full bg-[var(--website-secondary-color)] text-[var(--on-secondary)] px-6 py-3 rounded-lg font-bold text-lg transition duration-300 ease-in-out hover:scale-105 hover:shadow-lg hover:brightness-110"
            onClick={calculateCost}
          >
            Calculate Cost
          </button>

          {/* Result */}
          {cost !== null && (
            <div className="text-center mt-4">
              <p className="text-lg">Estimated Monthly Cost</p>
              <h2 className="text-3xl font-bold text-green-600">
                ₹ {cost.toLocaleString()}
              </h2>
            </div>
          )}

          {/* Footer note */}
          <div className="text-sm text-gray-500 text-center mt-4">
            *This is an estimated cost. Contact us for exact pricing.
          </div>
        </div>
      </div>
    </div>
  );
}

export default ColocationCalculator;
